/**
 * SalonAlanPlaniPage.jsx — seçili fuardaki bir salonun ALAN PLANI'nı (stand yerleşimi) tam düzenleme
 * modunda, Alan Planı katmanında açar (backlog #204 "Alan Planına Git", 2026-09-26).
 * Route: /Management/Salonlar/:salonId/AlanPlani
 *
 * Açıldığı yerler: Salonlar listesindeki "Alan Planı (Kroki)" rozetinin menüsü (#204) ve Salon
 * Kartı (#163). SalonPlaniDuzenlePage (#193) fuar alanının Salon Planı'nı açar; bu sayfa ise
 * FUARIN kendi planını: fdsvector /embed/stand-plan?fairId&hallId&mode=edit&activeLayer=stands
 * (#205). O uç dokümanı fuar konfigürasyonunun hallDocumentMap'inden çözer ve YOKSA OLUŞTURMAZ
 * (404) — bu yüzden "Krokiyi Aç" kapısı gerekmez; önce resolveStandPlanDocument ile yoklanır.
 *
 * Başlıkta Alan Planı'nın çizim durumu (#202, salon_to_fuar.alan_plani_durum) değiştirilebilir
 * ve "Stand Çizimi Tamamlandı" düğmesi durumu tamamlanma zamanıyla birlikte 2'ye çeker.
 * Yalnızca personel; salon bu fuara bağlı değilse plan açılmaz.
 *
 * Backlog #236 (2026-09-27): salonun Salon Planı TAMAMLANDI değilse (ya da salon fuar alanında
 * kayıtlı değilse — durum bilinmiyor) Alan Planı çizimi başlatılamaz. Sayfa adresle açılsa bile
 * düzenleyici yerine nedeni ve planın SALT OKUNUR hâlini gösterir; Çiziliyor / Tamamlandı ve
 * "Stand Çizimi Tamamlandı" kilitli (backend #235 de 409 döner). Çizim yok'a dönmek serbest.
 */
import React, { useContext, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardBody, Button, Spinner, Alert, Badge,
} from "reactstrap";
import { ArrowLeft, CircleCheck } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import KrokiVectorEmbed from "../../../Components/Kroki/KrokiVectorEmbed";
import {
  KROKI_LAYERS, KROKI_MODES, resolveStandPlanDocument,
} from "../../../services/krokiVectorService";
import { getFuarHMax } from "../../../services/fuarKuralService";
import { setAlanPlaniDurumu } from "../../../services/fuarAlaniSalonService";
import { isStaffAccount } from "../../../services/firmaSessionService";
// #653 / #655 / #658 (2026-11-01): the prompt while the Salon Planı is unfinished, the stands still to be drawn, the completion warning.
import { getCizilecekStandlar } from "../../../services/cizilecekStandService";
import { AlanPlaniEngelPaneli, YenidenCizilecekIsareti, ilkStandAdlari } from "../../../Components/FairAreas/cizilecek";
import {
  CIZIM_DURUM, CizimDurumuPill, alanPlaniEngeli, ALAN_PLANI_KILITLI_DURUMLAR,
} from "../../../Components/FairAreas/salonDurumlari";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const fmtTarih = (v) => (v ? new Date(v).toLocaleString(currentLocaleTag()) : null);

export default function SalonAlanPlaniPage() {
  const { t: tx } = useTranslation("yonetim");
  const { salonId } = useParams();
  const { selectedFuarId } = useContext(MainContext);
  const personel = isStaffAccount();
  // #655: Krokiler's "Çiz" opens this page with ?cizilecekStandId=<id>: the editor arms that stand on open (fdsvector &standToDraw=).
  const [params] = useSearchParams();
  const cizilecekStandId = /^\d+$/.test(params.get("cizilecekStandId") ?? "") ? params.get("cizilecekStandId") : null;
  // #658: the hall's stands with no drawing yet (the SAVED plan: it can lag the editor by seconds) and the editor's "all drawn" signal.
  const [cizilecek, setCizilecek] = useState(null);
  const [tumuCizildi, setTumuCizildi] = useState(false);

  const [hall, setHall] = useState(null);       // getFuarHMax → halls[i]; null = bu fuara bağlı değil
  const [fuarAlaniName, setFuarAlaniName] = useState(null);
  const [fuarAlaniId, setFuarAlaniId] = useState(null); // #236: Salon Planı'na bağlantı için
  const [probe, setProbe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => { document.title = i18n.t("yonetim:salonAlanPlani.alanPlaniFds"); }, []);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const [ctx, p, liste] = await Promise.all([
          getFuarHMax(selectedFuarId, { forceLive: true }),
          resolveStandPlanDocument({ fairId: selectedFuarId, hallId: salonId }),
          getCizilecekStandlar(selectedFuarId, salonId),   // never throws
        ]);
        if (ignore) return;
        setCizilecek(liste);
        setFuarAlaniName(ctx?.fuarAlaniName ?? null);
        setFuarAlaniId(ctx?.fuarAlaniId ?? null);
        setHall((ctx?.halls ?? []).find((h) => String(h.salonId) === String(salonId)) ?? null);
        setProbe(p);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [selectedFuarId, salonId, reloadToken]);

  // #658: re-read the list (a plan save reaches FDS-Backend a moment after the editor's save event).
  async function yenileCizilecek() {
    if (!selectedFuarId) return;
    setCizilecek(await getCizilecekStandlar(selectedFuarId, salonId));
  }

  async function changeDurum(durum) {
    setBusy(true);
    setError(null);
    try {
      const out = await setAlanPlaniDurumu(selectedFuarId, salonId, durum);
      if (out?.data) setHall((h) => ({ ...h, ...out.data }));
      else setReloadToken((t) => t + 1);
      setSuccessMsg(out?.message || tx("salonAlanPlani.alanPlaniDurumuKaydedildi"));
      yenileCizilecek();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // "Stand Çizimi Tamamlandı" iki adımlı: tarih + işaretleyen kaydedilir, yanlış bir tık geri
  // alınabilse de tamamlanma zamanını ezer.
  const [onayBekliyor, setOnayBekliyor] = useState(false);

  const salonAdi = hall?.salonName ?? `#${salonId}`;
  // #658: how many stands are still to be drawn (the list when we have it, else the hall row's count) and the first names for the warning.
  const cizilecekSayisi = cizilecek?.ok ? cizilecek.stands.length : Number(hall?.toDrawCount ?? 0) || 0;
  const cizilecekAdlari = ilkStandAdlari(cizilecek?.ok ? cizilecek.stands : [], 5);
  const tamamlandi = hall?.alanPlaniDurum === CIZIM_DURUM.TAMAMLANDI;
  const tamamlanmaTarihi = tamamlandi ? fmtTarih(hall?.alanPlaniDurumTarihi) : null;
  // #236: null = Alan Planı başlatılabilir; yoksa nedeni (düzenleyici açılmaz).
  const engel = hall ? alanPlaniEngeli(hall) : null;

  let govde;
  if (!selectedFuarId) {
    govde = <Alert color="warning" className="mb-0">{tx("salonAlanPlani.onceUsttenBirFuarSecin")}</Alert>;
  } else if (loading || (!hall && !error && probe === null)) {
    govde = <div className="text-center py-5"><Spinner color="primary" /></div>;
  } else if (!personel) {
    govde = <Alert color="warning" className="mb-0">{tx("salonAlanPlani.alanPlaniDuzenlemeYalnizcaPersonel")}</Alert>;
  } else if (!hall) {
    govde = (
      <Alert color="warning" className="mb-0">
        {tx("salonAlanPlani.salonBuFuardaAktifDegil", { salonAdi })}{" "}<Link to="/Management/Salonlar">{tx("salonAlanPlani.salonlar")}</Link>{" "}{tx("salonAlanPlani.sayfasindanAktifEdin")}
      </Alert>
    );
  } else if (probe?.reason === "not-mapped" || probe?.reason === "missing-id") {
    govde = (
      <Alert color="warning" className="mb-0">
        {tx("salonAlanPlani.buSalonunBuFuarIcin")}
      </Alert>
    );
  } else if (engel) {
    // #236: düzenleyici yok; plan (varsa) salt okunur görünür.
    govde = (
      <>
        {/* WAS (until #653): a small warning with the reason and a link —
        <Alert color="warning" className="small">
          <div className="fw-semibold mb-1">{tx("salonAlanPlani.alanPlaniCizimiBaslatilamaz")}</div>
          {engel}
          {fuarAlaniId != null && hall.registeredAtFairground !== false && (<> <Link to={`/Management/FairAreas/${fuarAlaniId}/Salonlar/${salonId}/Plan?katman=${KROKI_LAYERS.FLOORPLAN}`}>{tx("salonAlanPlani.salonPlaniNiAc")}</Link></>)}
        </Alert>
        Now a prominent prompt with the Salon Planı and Altyapı Planı statuses and the button that opens the Salon Planı. */}
        <AlanPlaniEngelPaneli hall={hall} salonId={salonId} fuarAlaniId={fuarAlaniId} engel={engel} />
        <KrokiVectorEmbed
          fairId={selectedFuarId}
          hallId={salonId}
          hallName={hall.salonName ?? undefined}
          defaultMode={KROKI_MODES.VIEW}
          allowEdit={false}
          // WAS: viewOptions={{ layers: "none", forceLabels: true, fit: "hall" }}
          // #653 (fdsvector #652): the Alan Planı layer ACTIVE (and empty), the Salon Planı and the Altyapı Planı passive behind it.
          viewOptions={{ activeLayer: KROKI_LAYERS.STANDS, forceLabels: true, fit: "hall" }}
          height="calc(100vh - 400px)"
        />
      </>
    );
  } else {
    govde = (
      <>
        {probe && !probe.ok && (
          <Alert color="warning" className="py-2 small">
            {tx("salonAlanPlani.planOncedenDogrulanamadiGomuluUygulama", { message: probe.message })}
          </Alert>
        )}
        <KrokiVectorEmbed
          fairId={selectedFuarId}
          hallId={salonId}
          hallName={hall.salonName ?? undefined}
          defaultMode={KROKI_MODES.EDIT}
          allowEdit
          activeLayer={KROKI_LAYERS.STANDS}
          standToDraw={cizilecekStandId}
          onAllStandsDrawn={() => setTumuCizildi(true)}
          onSaved={() => { setTimeout(yenileCizilecek, 2500); }}
          height="calc(100vh - 330px)"
        />
      </>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("salonAlanPlani.alanPlani")} pageTitle={tx("salonAlanPlani.salonlarVeKrokiler")} />

        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
        {successMsg && <Alert color="success">{successMsg}</Alert>}
        {/* #658: the editor says every stand has a drawing - offer the (two-step) completion; it is not done for the user. */}
        {tumuCizildi && hall && personel && !engel && !tamamlandi && !onayBekliyor && (
          <Alert color="success" className="d-flex align-items-center gap-2 flex-wrap py-2" toggle={() => setTumuCizildi(false)}>
            <CircleCheck size={16} />
            <span>{tx("cizilecek.tumuCizildiBanner")}</span>
            <Button size="sm" color="success" disabled={busy} onClick={() => setOnayBekliyor(true)}>{tx("cizilecek.bannerIsaretle")}</Button>
          </Alert>
        )}

        <Card className="mb-3">
          <CardBody className="py-2 d-flex align-items-center gap-3 flex-wrap">
            <Link to="/Management/Salonlar" className="btn btn-light btn-sm">
              <ArrowLeft size={14} className="me-1" />{tx("salonAlanPlani.salonlar")}
            </Link>
            <div className="flex-grow-1">
              <h5 className="mb-0">
                {tx("salonAlanPlani.salonAlanPlani", { salonAdi })}
                {engel
                  ? <Badge color="light" pill className="ms-2 align-middle text-muted border">{tx("salonAlanPlani.saltOkunur")}</Badge>
                  : <Badge color="warning" pill className="ms-2 align-middle">{tx("salonAlanPlani.duzenleme")}</Badge>}
              </h5>
              <div className="text-muted small">
                {fuarAlaniName ?? tx("salonAlanPlani.fuarAlani")}{" "}{tx("salonAlanPlani.fuarBuFuarinStandYerlesimi", { selectedFuarId })}
                {" · "}<Link to={`/Management/Salonlar/${salonId}`}>{tx("salonAlanPlani.salonKarti")}</Link>
              </div>
            </div>
            {hall && personel && (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="text-muted small">{tx("salonAlanPlani.cizimDurumu")}</span>
                <CizimDurumuPill
                  id="alan-plani-sayfa-durum" plan="alanPlani"
                  durum={hall.alanPlaniDurum ?? 0} tarih={hall.alanPlaniDurumTarihi}
                  busy={busy} disabled={busy}
                  menuHeader={tx("salonAlanPlani.cizimDurumunuDegistir")}
                  lockedDurums={engel ? ALAN_PLANI_KILITLI_DURUMLAR : null} lockedReason={engel}
                  onChange={changeDurum}
                />
                {tamamlandi ? (
                  <>
                    <span className="small text-success">
                      <CircleCheck size={14} className="me-1" />{tx("salonAlanPlani.standCizimiTamamlandi")}{tamamlanmaTarihi ? `: ${tamamlanmaTarihi}` : ""}
                    </span>
                    {/* #658: a stand added after completion flags the hall again; the status NEVER changes by itself - "Çizime devam et" is the user's call. */}
                    <YenidenCizilecekIsareti hall={{ ...hall, toDrawCount: cizilecekSayisi }} onDevam={() => changeDurum(CIZIM_DURUM.CIZILIYOR)} busy={busy} />
                  </>
                ) : onayBekliyor ? (
                  <>
                    {/* #658: the confirmation says how many stands have no drawing yet - it WARNS, it never refuses (decision D6). */}
                    {cizilecek?.ok && cizilecekSayisi > 0 && (
                      <span className="small text-warning">
                        {tx("cizilecek.onayUyari", { sayi: cizilecekSayisi, adlar: cizilecekAdlari.adlar.join(", ") })}
                        {cizilecekAdlari.kalan > 0 && <>{" "}{tx("cizilecek.onayUyariKalan", { kalan: cizilecekAdlari.kalan })}</>}
                      </span>
                    )}
                    <Button size="sm" color={cizilecek?.ok && cizilecekSayisi > 0 ? "warning" : "success"} disabled={busy}
                      onClick={() => { setOnayBekliyor(false); changeDurum(CIZIM_DURUM.TAMAMLANDI); }}>
                      {cizilecek?.ok && cizilecekSayisi > 0 ? tx("cizilecek.yineDeTamamla") : tx("salonAlanPlani.evetTamamlandiOlarakIsaretle")}
                    </Button>
                    <Button size="sm" color="light" disabled={busy} onClick={() => setOnayBekliyor(false)}>{tx("salonAlanPlani.vazgec")}</Button>
                  </>
                ) : (
                  // #236: devre dışı düğme title göstermez — nedeni saran span'da.
                  <span title={engel ?? undefined}>
                    <Button size="sm" color="success" outline disabled={busy || Boolean(engel)} onClick={() => setOnayBekliyor(true)}>
                      <CircleCheck size={14} className="me-1" />{tx("salonAlanPlani.standCizimiTamamlandi2")}
                    </Button>
                  </span>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>{govde}</CardBody>
        </Card>
      </Container>
    </div>
  );
}
